import type {ConnectionOptions, JitsiTrackState} from '../pages/stream/types/lib-jitsi-meet';
import type {StreamingInfo, TStudioMode} from '../pages/stream/types';
import {jwtDecode} from "jwt-decode";

export interface ILayOut {
    gridCol: number;
    gridRow: number;
    studioCol: number;
    studioColSpan: number;
    studioRow: number;
    studioRowSpan: number;
    localCol: number;
    localColSpan: number;
    localRow: number;
    localRowSpan: number;
    remoteSize: number;
}

export const getCellLayout = (sMode: TStudioMode): ILayOut => {
    if (sMode === 'pip') {
        return {
            gridCol: 12,
            gridRow: 12,
            studioCol: 1,
            studioColSpan: 12,
            studioRow: 1,
            studioRowSpan: 12,
            localCol: 9,
            localColSpan: 4,
            localRow: 9,
            localRowSpan: 4,
            remoteSize: 0,
        };
    } else if (sMode === '4') {
        return {
            gridCol: 2,
            gridRow: 2,
            studioCol: 2,
            studioColSpan: 1,
            studioRow: 1,
            studioRowSpan: 1,
            localCol: 2,
            localColSpan: 1,
            localRow: 2,
            localRowSpan: 1,
            remoteSize: 2,
        };
    } else if (sMode === '22') {
        return {
            gridCol: 6,
            gridRow: 8,
            studioCol: 1,
            studioColSpan: 5,
            studioRow: 1,
            studioRowSpan: 4,
            localCol: 6,
            localColSpan: 3,
            localRow: 1,
            localRowSpan: 2,
            remoteSize: 22,
        };
    } else if (sMode === '28') {
        return {
            gridCol: 9,
            gridRow: 8,
            studioCol: 2,
            studioColSpan: 6,
            studioRow: 1,
            studioRowSpan: 7,
            localCol: 4,
            localColSpan: 2,
            localRow: 8,
            localRowSpan: 2,
            remoteSize: 26,

        };
    } else if (sMode === 'direct-chat') {
        return {
            gridCol: 12,
            gridRow: 12,
            studioCol: 1,
            studioColSpan: 8,
            studioRow: 3,
            studioRowSpan: 8,
            localCol: 9,
            localColSpan: 4,
            localRow: 5,
            localRowSpan: 4,
            remoteSize: 0,
        };
    } else {
        return {
            gridCol: 12,
            gridRow: 12,
            studioCol: 1,
            studioColSpan: 12,
            studioRow: 1,
            studioRowSpan: 12,
            localCol: 11,
            localColSpan: 2,
            localRow: 11,
            localRowSpan: 2,
            remoteSize: 0,
        };
    }
};

export const buildOptions = (appId: string, room: string): ConnectionOptions => {
    return {
        hosts: {
            domain: '8x8.vc',
            muc: `conference.${appId}.8x8.vc`,
        },
        serviceUrl: `wss://8x8.vc/${appId}/xmpp-websocket?room=${room}`,
        websocketKeepAliveUrl: `https://8x8.vc/${appId}/_unlock?room=${room}`,
    };
};

export const isVideoPlaying = (videoElement: HTMLVideoElement) => {
    return (
        videoElement &&
        videoElement.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA &&
        !videoElement.paused &&
        !videoElement.ended &&
        videoElement.currentTime > 0
    );
};

export const getAverageFrameBrightness = (videoElement: HTMLVideoElement, canvas: HTMLCanvasElement) => {
    const context = canvas.getContext('2d')!;
    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    const frame = context.getImageData(0, 0, canvas.width, canvas.height);
    let sum = 0;
    for (let i = 0; i < frame.data.length; i += 4) {
        const r = frame.data[i];
        const g = frame.data[i + 1];
        const b = frame.data[i + 2];
        const brightness = (r + g + b) / 3;
        sum += brightness;
    }
    return sum / (frame.data.length / 4);
};

export const isVideoVisiblyDark = (videoElement: HTMLVideoElement) => {
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth || 240;
    canvas.height = videoElement.videoHeight || 180;
    const context = canvas.getContext('2d');
    if (!context) return false;
    const brightness = getAverageFrameBrightness(videoElement, canvas);
    return brightness < 10;
};

export const evaluateTrackPriority = (
    trackId: string,
    trackState: JitsiTrackState,
    videoElement?: HTMLVideoElement
): number => {
    let priority = 0;
    if (videoElement) {
        if (videoElement && trackState.videoTrack && isVideoPlaying(videoElement)) {
            priority += 100;
        }
        if (videoElement && trackState.videoTrack && !isVideoVisiblyDark(videoElement)) {
            priority += 50;
        }
    } else {
        if (trackState.videoTrack) {
            priority += 150;
        }
    }
    if (trackState.audioTrack) {
        priority += 10;
    }
    if (trackState.videoTrack === null) {
        priority -= 200;
    }
    return priority;
};

export const redTeam = [1, 4, 5, 8, 9, 12, 13, 16, 17, 20, 21, 23, 25, 27, 29, 32, 34, 36, 38, 40];
export const blueTeam = [2, 3, 6, 7, 10, 11, 14, 15, 18, 19, 22, 24, 26, 28, 30, 31, 33, 35, 37, 39];

export const getMappingTable = (index: number) => {
    return redTeam.includes(index);
};

export const isBroadcastOver = (streamingData: Array<StreamingInfo>) => {
    const lastStream = streamingData[streamingData.length - 1];
    const now = new Date();
    const one = String(now.getHours()).padStart(2, '0');
    const two = lastStream?.end_time.split(':')[0];
    return one > two;
};

export type TNetworkStatus = '대기' | '나쁨' | '보통' | '좋음';

export const checkNetworkStatus = async (url: string): Promise<TNetworkStatus> => {
    const start = performance.now();
    try {
        const res = await fetch(url, {method: 'HEAD', cache: 'no-cache'});
        const latency = performance.now() - start;

        if (!res.ok) return '나쁨';
        if (latency < 200) return '좋음';
        if (latency < 500) return '보통';
        return '나쁨';
    } catch {
        return '나쁨';
    }
};

// ===== Jitsi 토큰 캐시 (메모리 전용) =====
// [gs #53] 이 토큰은 moderator 권한 토큰이다. localStorage에 두면 경로당에 놓인 셋톱박스에
// 손이 닿거나 화면이 캡처되는 것만으로 그대로 새어 나가고, 기기를 껐다 켜도 만료(2시간)
// 전까지 살아 있다. 프로세스 메모리에만 두면 노출 구간이 지금 켜져 있는 세션으로 줄어든다.
// 재시작·새로고침으로 캐시가 비면 setJitsiToken이 새로 발급받는다 — 이 재발급 경로는
// 원래도 첫 부팅과 2시간마다의 만료에서 늘 도는 길이라 새로 만드는 경로가 아니다.
// (연결·재연결은 store의 state.jwt만 읽으므로 저장 위치가 재연결 경로에 영향을 주지 않는다.)
let jitsiTokenCache: string | null = null;

// 이미 배포된 기기에는 예전 버전이 저장해 둔 토큰이 그대로 남아 있다. 새 버전이 더 이상
// 그 키를 읽지 않는다고 해서 값이 사라지지는 않으므로(만료돼도 문자열은 남는다) 시작할 때 지운다.
try {
    localStorage.removeItem('jitsi_token');
} catch {
    // localStorage를 못 쓰는 환경이면 지울 것도 없다.
}

/** 발급받은 Jitsi 토큰을 메모리에 캐시한다(디스크에 남기지 않는다). */
export const setCachedJitsiToken = (token: string | null): void => {
    jitsiTokenCache = token;
};

// 메모리에 캐시된 토큰을 돌려주되, 만료된 토큰은 버리고 null 반환
export const getValidCachedToken = (): string | null => {
    if (!jitsiTokenCache) return null;
    try {
        const decoded = jwtDecode(jitsiTokenCache);
        const expTime = (decoded.exp ?? 0) * 1000;
        const timeLeft = expTime - Date.now();
        if (timeLeft > 1000) {
            return jitsiTokenCache;
        }
        // 만료된 토큰은 버린다
        jitsiTokenCache = null;
        return null;
    } catch {
        jitsiTokenCache = null;
        return null;
    }
};